/**
 * Hook: useAppealContentReport
 *
 * Holds form state and submits a content report appeal.
 * Mirrors Flutter's ContentReportAppealScreenState
 * (lib/presentation/screens/content/content_report_appeal_screen.dart).
 */

import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { appealContentReportUseCase } from '@/domain/usecases/report/appealContentReportUseCase';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseAppealContentReportProps {
  contentId: string;
  onFinish: (submitted: boolean) => void;
}

export interface UseAppealContentReportResult {
  comment: string;
  isSubmitting: boolean;
  hasCommentError: boolean;
  setComment: (value: string) => void;
  handleSubmit: () => Promise<void>;
}

export function useAppealContentReport({
  contentId,
  onFinish,
}: UseAppealContentReportProps): UseAppealContentReportResult {
  const { l10n } = useLocale();

  const [comment, setCommentState] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setComment = useCallback((value: string) => {
    setIsDirty(true);
    setCommentState(value);
  }, []);

  const hasCommentError = isDirty && comment.trim().length === 0;

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (comment.trim().length === 0) return;

    setIsSubmitting(true);
    try {
      const success = await appealContentReportUseCase.execute(contentId, comment);
      if (success) {
        toast.success(l10n.appealContentReport.submitSuccess);
        onFinish(true);
      } else {
        toast.error(l10n.appealContentReport.submitError);
      }
    } catch (err) {
      appLogger.error('Error submitting appeal', 'useAppealContentReport.handleSubmit', { error: err as Error });
      toast.error(l10n.appealContentReport.submitError);
    } finally {
      setIsSubmitting(false);
    }
  }, [comment, contentId, isSubmitting, onFinish, l10n]);

  return {
    comment,
    isSubmitting,
    hasCommentError,
    setComment,
    handleSubmit,
  };
}
